import React from 'react';
import {
    Button,
    SpaceBetween,
    StatusIndicator,
    BreadcrumbGroup,
    TextContent,
    Header,
    Container,
    Link,
    Alert,
    Box,
} from '@amzn/awsui-components-console';
import { copyToClipboard } from '../../lib/utils';
import '../../../styles/custom.scss';
import CsvDownloader from 'react-csv-downloader';
import { credToCSVData } from '../apis';
import { credentials_columns } from '../../lib/constants';

export default function HomeLabSetup() {
    /**
     * Home Lab Setup component
     */
    var stage = 'beta';
    stage = stage === 'test' ? 'alpha' : stage;
    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'Amazon Device Farm', href: '#/' },
                    {
                        text: 'Home lab setup',
                        href: '#/home-lab-management/setup',
                    },
                ]}
            />
            <Header
                variant="h1"
                description="Home lab setup allows sharing android devices at your home with Amazon Device Farm community with in Amazon."
            >
                Welcome to Amazon Device Farm - Home lab setup
            </Header>

            <Container header={<Header variant="h2">Home lab setup prerequisites</Header>}>
                <TextContent>
                    <ul>
                        <li>Operating system Ubuntu 22.04. Amazon CORP images are not supported.</li>
                        <li>
                            <a href="https://www.gnu.org/software/libc/">GNU C Library </a>
                            (glibc) version 2.25 or greater
                        </li>
                        <li>At least 4 GB free disk space</li>
                        <li>At least 1 GB ram</li>
                        <li>Internet connection with bandwidth of at least 500 Mbps</li>
                        <li>Sudo permission on ubuntu system</li>
                    </ul>
                </TextContent>
            </Container>
            <Container header={<Header variant="h2">Home lab pre-provisioning steps</Header>}>
                <TextContent>
                    <ol>
                        <li>
                            <SpaceBetween size="xs">
                                <p>
                                    Create your unique Lab ID to identify your home lab setup, or use an existing lab if
                                    already created. This Lab ID will be used to track and manage your lab
                                    configuration. <Link href="#/lab-management/labs">Open Labs</Link> to view all
                                    available labs and manage existing ones.
                                </p>
                            </SpaceBetween>
                        </li>
                        <li>
                            <SpaceBetween size="xs">
                                <p>
                                    Hosting personal system in Amazon Device Farm lab requires authentication. You can
                                    generate personalized one time credentials using the generate credentials button
                                    below, which will be{' '}
                                    <StatusIndicator type="info">valid for one hour</StatusIndicator>.
                                </p>
                                <CsvDownloader
                                    filename="home-lab-credential"
                                    extension=".csv"
                                    columns={credentials_columns}
                                    datas={credToCSVData}
                                >
                                    <Button iconName="download" variant="primary">
                                        Generate credentials
                                    </Button>
                                </CsvDownloader>
                                <Alert className="custom-alert" header="Important note">
                                    Do not share or distribute these credentials within or outside Amazon.
                                </Alert>
                            </SpaceBetween>
                        </li>
                        <li>
                            <p>
                                Install required AWS CLI V2 using{' '}
                                <a href="https://docs.aws.amazon.com/cli/latest/userguide/getting-started-install.html">
                                    AWS CLI Install Instructions{' '}
                                </a>
                                and verify it is successful with below command in terminal.
                            </p>
                            {copyToClipboard('aws --version')}
                        </li>
                        <li>
                            <p>
                                Amazon Device Farm uses Ansible to provision host as an edge. Follow below steps to setup
                                Ansible.
                            </p>
                            <p>Install Ansible and other dependencies.</p>
                            {copyToClipboard(
                                'sudo apt-get install wget unzip curl net-tools ansible git python3-pip -y'
                            )}
                        </li>
                        <li>
                            <p>Setup AWS credentials for AWS CLI using generated credentials in step 1.</p>
                            {copyToClipboard('export AWS_ACCESS_KEY_ID=<access key id from downloaded credentials>')}
                            {copyToClipboard(
                                'export AWS_SECRET_ACCESS_KEY=<secret access key from downloaded credentials>'
                            )}
                            {copyToClipboard('export AWS_SESSION_TOKEN=<session token from downloaded credentials>')}
                        </li>
                        <li>
                            <p>Download ansible setup wizard.</p>
                            {copyToClipboard(
                                `aws s3 cp s3://ds2-devicefarm-host-provisioning-${stage}/home_lab_provisioning.yml <your directory to download the file>/config.yml`
                            )}
                        </li>
                    </ol>
                </TextContent>
            </Container>
            <Container header={<Header variant="h2">Home lab provisioning</Header>}>
                <TextContent>
                    <ol>
                        <li>
                            <p>
                                Navigate to directory where setup wizard is downloaded and execute it using below
                                ansible command.
                            </p>
                            {copyToClipboard(
                                'sudo ansible-playbook -v --e "aws_access_key_id=$AWS_ACCESS_KEY_ID aws_secret_access_key=$AWS_SECRET_ACCESS_KEY aws_session_token=$AWS_SESSION_TOKEN" <your directory to download the file>/config.yml'
                            )}
                        </li>
                        <li>
                            <p>Use below guide to provide responses to setup wizard prompts.</p>
                            <div className="custom-code">
                                <Box variant="code">
                                    [sudo] password for &lt;user&gt;: &lt;Enter your password&gt;
                                    <br />
                                    Enter your AWS region (i.e. us-east-1): &lt;Enter us-west-2&gt;
                                    <br />
                                    Enter the owner of the host: &lt;Enter you alias/first name&gt;
                                    <br />
                                    Enter the location of your host [Seattle, Chennai, Asia, Europe]: &lt;Enter
                                    location&gt;
                                    <br />
                                    Enter the lab ID for this host: &lt;Prod_DF_TEST&gt;
                                </Box>
                            </div>
                        </li>
                        <li>
                            Follow steps from this{' '}
                            <Link
                                external
                                href="https://w.amazon.com/bin/view/DS2DeviceFarm/Documents/ReadMe/HomeLabRegisterKats/"
                            >
                                wiki
                            </Link>{' '}
                            to register your KATS host agent.
                        </li>
                        <li>
                            To verify your home lab is established successfully refer to{' '}
                            <Link href="#/home-lab-management/monitor">Home Lab Monitoring</Link>.
                        </li>
                    </ol>
                </TextContent>
            </Container>
        </SpaceBetween>
    );
}
